// Boomin' — My Page / profile screen (/me)
//
// Redesigned replacement for the legacy /users/profile dashboard. Shows the
// signed-in user's profile, favorite count, and a menu into the rest of the
// app (マイクーポン, 編集, ログアウト).

import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useHistory } from "react-router-dom";
import {
  BRAND,
  BRAND_SOFT,
  PictX,
  PictName,
  BottomNavX,
  AppHeader,
  AppFooter,
  useBoominFx,
} from "./shared";
import { DEFAULT_USER_IMAGE } from "../../Constants";
import {
  UserProfile,
  fetchProfile,
  isUserLoggedIn,
  logoutUser,
} from "./api";

const brand = BRAND;
const brandSoft = BRAND_SOFT;

const genderLabel = (g: string): string =>
  g === "male" ? "男性" : g === "female" ? "女性" : "未設定";

const areaLabel = (u: UserProfile): string => {
  const parts = [u.state, u.city].filter(Boolean);
  return parts.length ? parts.join("・") : "未設定";
};

export const MeScreen: React.FC = () => {
  useBoominFx();
  const history = useHistory();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [favoriteCount, setFavoriteCount] = useState<number>(0);

  const loggedIn = isUserLoggedIn();

  const load = useCallback(async () => {
    if (!loggedIn) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetchProfile();
      setProfile(res.user);
      setFavoriteCount(res.favorite_count);
    } catch (e: any) {
      setError(typeof e === "string" ? e : e?.message || "読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  }, [loggedIn]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <Helmet>
        <title>マイページ | Boomin'</title>
      </Helmet>
      <AppHeader />
      <div
        style={{
          background: "#FFFBF7",
          minHeight: "100vh",
          paddingBottom: 0,
          position: "relative",
        }}
      >
        <MeHeader />

        {!loggedIn ? (
          <SignInPrompt />
        ) : error ? (
          <ErrorState message={error} onRetry={load} />
        ) : loading || !profile ? (
          <ProfileSkeleton />
        ) : (
          <>
            <ProfileCard profile={profile} favoriteCount={favoriteCount} />

            <div
              style={{
                padding: "8px 20px 0",
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <MenuRow
                icon="ticket"
                label="マイクーポン"
                sub="保存・使用したクーポン"
                onClick={() => history.push("/wallet")}
              />
              <MenuRow
                icon="user"
                label="プロフィールを編集"
                sub="お名前・連絡先・お住まいなど"
                onClick={() => history.push("/me/edit")}
              />
            </div>

            <div style={{ padding: "20px 20px 0" }}>
              <button
                onClick={() => logoutUser()}
                style={{
                  width: "100%",
                  height: 48,
                  borderRadius: 999,
                  border: "1px solid #F0E6DC",
                  background: "#fff",
                  color: "#A6321E",
                  fontSize: 14,
                  fontWeight: 800,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                ログアウト
              </button>
            </div>
          </>
        )}

        <AppFooter withBottomNav />
      </div>

      <BottomNavX active="me" brand={brand} />
    </>
  );
};

const MeHeader: React.FC = () => (
  <div
    style={{
      padding: "calc(env(safe-area-inset-top, 0px) + 28px) 20px 0",
      position: "relative",
    }}
  >
    <div
      style={{
        fontSize: 11,
        color: "#888",
        fontWeight: 700,
        letterSpacing: "0.12em",
        marginBottom: 4,
      }}
    >
      YOUR ACCOUNT
    </div>
    <h1
      style={{
        fontSize: 30,
        fontWeight: 800,
        margin: 0,
        letterSpacing: "0.01em",
        lineHeight: 1.15,
      }}
    >
      マイページ<span style={{ color: brand }}>。</span>
    </h1>
    <div
      style={{
        position: "absolute",
        right: 16,
        top: "calc(env(safe-area-inset-top, 0px) + 24px)",
        opacity: 0.5,
      }}
    >
      <PictX name="user" size={28} stroke={brand} strokeWidth={1.6} />
    </div>
  </div>
);

const ProfileCard: React.FC<{ profile: UserProfile; favoriteCount: number }> = ({
  profile,
  favoriteCount,
}) => (
  <div style={{ padding: "20px 20px 0" }}>
    <div
      style={{
        background: "#fff",
        borderRadius: 22,
        border: "1px solid #F0E6DC",
        boxShadow: "0 8px 22px rgba(0,0,0,0.06)",
        overflow: "hidden",
      }}
    >
      {/* Identity band */}
      <div
        style={{
          background: `linear-gradient(135deg, ${brand} 0%, #FF7A52 100%)`,
          padding: "20px 18px",
          display: "flex",
          alignItems: "center",
          gap: 14,
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 999,
            overflow: "hidden",
            flexShrink: 0,
            border: "2px solid rgba(255,255,255,0.85)",
            background: "#fff",
          }}
        >
          <img
            src={profile.profile_image || DEFAULT_USER_IMAGE}
            alt={profile.user_name || "プロフィール"}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              color: "#fff",
              fontSize: 20,
              fontWeight: 800,
              lineHeight: 1.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {profile.user_name || "ゲスト"}
          </div>
          <div
            style={{
              marginTop: 4,
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              background: "rgba(255,255,255,0.25)",
              color: "#fff",
              fontSize: 11,
              fontWeight: 700,
              padding: "3px 9px",
              borderRadius: 999,
              backdropFilter: "blur(4px)",
            }}
          >
            <PictX name="heart" size={12} stroke="#fff" strokeWidth={2} />
            お気に入り {favoriteCount} 件
          </div>
        </div>
      </div>

      {/* Detail rows */}
      <div>
        <InfoRow icon="pin" label="お住まい" value={areaLabel(profile)} />
        <InfoRow icon="bell" label="メール" value={profile.email || "未設定"} />
        <InfoRow icon="user" label="電話番号" value={profile.phone || "未設定"} />
        <InfoRow
          icon="sparkle"
          label="性別"
          value={genderLabel(profile.gender)}
          last
        />
      </div>
    </div>
  </div>
);

const InfoRow: React.FC<{
  icon: PictName;
  label: string;
  value: string;
  last?: boolean;
}> = ({ icon, label, value, last }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "13px 16px",
      borderTop: "1px solid #F4ECE2",
      borderBottom: last ? "none" : undefined,
    }}
  >
    <PictX name={icon} size={18} stroke={brand} strokeWidth={1.8} />
    <span style={{ fontSize: 12, color: "#888", fontWeight: 700, width: 72, flexShrink: 0 }}>
      {label}
    </span>
    <span
      style={{
        fontSize: 13,
        fontWeight: 600,
        color: "#1A1A1A",
        textAlign: "right",
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </span>
  </div>
);

const MenuRow: React.FC<{
  icon: PictName;
  label: string;
  sub: string;
  onClick: () => void;
}> = ({ icon, label, sub, onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      width: "100%",
      background: "#fff",
      border: "1px solid #F0E6DC",
      borderRadius: 16,
      padding: "14px 16px",
      cursor: "pointer",
      textAlign: "left",
      fontFamily: "inherit",
    }}
  >
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: 10,
        background: brandSoft,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <PictX name={icon} size={18} stroke={brand} strokeWidth={1.8} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: "#1A1A1A" }}>{label}</div>
      <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{sub}</div>
    </div>
    <PictX name="chevron" size={18} stroke="#C9BCAE" strokeWidth={2.2} />
  </button>
);

const SignInPrompt: React.FC = () => (
  <div
    style={{
      margin: "32px 20px 0",
      padding: "28px 22px",
      background: "#fff",
      border: "1px solid #F0E6DC",
      borderRadius: 22,
      textAlign: "center",
      lineHeight: 1.7,
    }}
  >
    <div style={{ fontSize: 32, marginBottom: 8 }}>👤</div>
    <div style={{ fontSize: 16, fontWeight: 800, color: "#1A1A1A", marginBottom: 6 }}>
      ログインしてはじめよう
    </div>
    <div style={{ fontSize: 12, color: "#666", marginBottom: 18 }}>
      ログインすると、プロフィールやお気に入りの
      <br />
      クーポンを管理できます。
    </div>
    <a
      href="/public/oauth/instabase"
      style={{
        display: "inline-block",
        background: brand,
        color: "#fff",
        padding: "12px 28px",
        borderRadius: 999,
        fontSize: 14,
        fontWeight: 800,
        textDecoration: "none",
        boxShadow: `0 6px 18px ${brand}55`,
      }}
    >
      ログイン / 新規登録
    </a>
  </div>
);

const ProfileSkeleton: React.FC = () => (
  <div style={{ padding: "20px 20px 0" }}>
    <div
      style={{
        height: 260,
        borderRadius: 22,
        background: "#fff",
        border: "1px solid #F0E6DC",
      }}
    />
  </div>
);

const ErrorState: React.FC<{ message: string; onRetry: () => void }> = ({
  message,
  onRetry,
}) => (
  <div
    style={{
      margin: "20px 20px 0",
      padding: "20px",
      background: "#fff",
      border: "1px solid #FFDAD0",
      borderRadius: 18,
      color: "#A6321E",
      fontSize: 13,
      lineHeight: 1.6,
    }}
  >
    <div style={{ marginBottom: 10 }}>{message}</div>
    <button
      onClick={onRetry}
      style={{
        background: brand,
        color: "#fff",
        border: 0,
        borderRadius: 999,
        padding: "8px 16px",
        fontWeight: 700,
        cursor: "pointer",
        fontFamily: "inherit",
      }}
    >
      もう一度
    </button>
  </div>
);

export default MeScreen;
